import LegalLayout from "./LegalLayout";
import { ShieldCheck, Lock, Youtube, Database, Sparkles, CheckCircle2, Mail } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="How Virality AI collects, protects, uses, and respects your content data and connected platform analytics."
      lastUpdated="September 10, 2026"
    >
      <div className="space-y-8 text-sm leading-relaxed text-muted-foreground">
        {/* Intro */}
        <div>
          <p className="text-base text-foreground font-medium">
            Welcome to Virality AI (&ldquo;Virality&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;). We believe that creative independence starts with privacy and ownership. This Privacy Policy explains how we collect, store, process, and safeguard your personal information, connected social channel data, and generated creative assets when you use the Virality platform.
          </p>
        </div>

        {/* Highlight Card */}
        <div className="rounded-xl border border-accent/30 bg-accent/5 p-5">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-accent shrink-0 mt-0.5" />
            <div>
              <h4 className="font-display font-bold text-foreground text-sm">
                Our Core Privacy Commitments
              </h4>
              <ul className="mt-2 space-y-1.5 text-xs text-foreground/85">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-accent shrink-0" />
                  <span><strong>You own your content:</strong> Every script, hook, and idea created in your workspace belongs entirely to you.</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-accent shrink-0" />
                  <span><strong>We never sell your data:</strong> We do not sell, rent, or trade your personal information or creator metrics to advertisers or data brokers.</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-accent shrink-0" />
                  <span><strong>Zero AI training on private data:</strong> Your connected YouTube/Instagram metrics and private drafts are never used to train public AI foundation models.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 1 */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            1. Information We Collect
          </h2>
          <p>
            To provide intelligent content recommendations, scripts, and channel telemetry, we collect information across three main categories:
          </p>
          <div className="space-y-3 pl-2">
            <div>
              <h3 className="font-semibold text-foreground">a. Account and Profile Information</h3>
              <p className="text-xs mt-1">
                When you register for an account or join our waitlist, we collect your name, email address, password hash, and billing identifier. If you contact support, we retain customer support correspondence.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-foreground">b. Creator DNA &amp; Workspace Inputs</h3>
              <p className="text-xs mt-1">
                Information you voluntarily provide about your creative niche, audience demographics, target video length, tone of voice, content pillars, draft scripts, hooks, and production workspace status.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-foreground">c. Connected Platform Data (YouTube &amp; Social Integrations)</h3>
              <p className="text-xs mt-1">
                When you authenticate your YouTube channel or social accounts via OAuth, we collect authorized telemetry such as subscriber counts, recent video views, retention indicators, and publishing cadence to generate personalized growth recommendations.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-foreground">d. Technical &amp; Diagnostic Telemetry</h3>
              <p className="text-xs mt-1">
                Browser type, operating system, IP address, general geographic region, session timestamps, and product usage interactions (such as completing onboarding or the interactive tour).
              </p>
            </div>
          </div>
        </section>

        {/* Section 2: Google & YouTube Compliance */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground flex items-center gap-2">
            <Youtube className="h-4 w-4 text-red-500" /> 2. Google API Services &amp; YouTube Data Disclosure
          </h2>
          <div className="rounded-xl border border-border bg-surface p-4 text-xs space-y-2.5">
            <p className="font-semibold text-foreground">
              Google API Services User Data Policy Compliance
            </p>
            <p>
              Virality&rsquo;s use and transfer to any other app of information received from Google APIs adheres to the{" "}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline font-medium"
              >
                Google API Services User Data Policy
              </a>
              , including the <strong>Limited Use</strong> requirements.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Limited Read Access:</strong> We request read-only access to YouTube analytics and video metadata solely to power the Virality Analytics dashboard and generate audience-aligned video topic recommendations.
              </li>
              <li>
                <strong>No Public Model Training:</strong> Google and YouTube user data is strictly isolated to your account. We never feed your YouTube performance metrics or private channel data into general public AI training corpora.
              </li>
              <li>
                <strong>Revocation of Access:</strong> You can disconnect your YouTube account at any time directly in your <span className="font-semibold text-foreground">Connections</span> page or via Google&rsquo;s third-party permissions dashboard at{" "}
                <a
                  href="https://myaccount.google.com/permissions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent underline"
                >
                  myaccount.google.com/permissions
                </a>.
              </li>
            </ul>
          </div>
        </section>

        {/* Section 3: AI Model Inference */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent" /> 3. How We Use Artificial Intelligence
          </h2>
          <p>
            Virality utilizes enterprise AI APIs (such as Anthropic Claude, OpenAI, and Google Gemini) to assist you with script drafting, hook scoring, and viral angle ideation.
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-xs">
            <li>
              <strong>Enterprise Zero Data Retention:</strong> Requests sent to our AI model providers are governed by enterprise commercial agreements prohibiting the model providers from using your prompts or workspace drafts to train their foundation models.
            </li>
            <li>
              <strong>Human in the Loop:</strong> All suggestions generated by our AI tools are recommendations. You retain complete editorial control and ownership over what you choose to adopt, edit, or publish.
            </li>
          </ul>
        </section>

        {/* Section 4: Sharing & Sub-processors */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            4. How We Share Information
          </h2>
          <p>
            We do not sell, rent, or trade your personal information. We only share information with vetted third-party service providers (&ldquo;Sub-processors&rdquo;) necessary to operate the service:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div className="rounded-lg border border-border p-3 bg-surface">
              <span className="font-bold text-foreground block">Cloud Infrastructure &amp; Database</span>
              <span className="text-muted-foreground mt-0.5 block">Encrypted data storage and compute hosting (Render, Supabase, AWS).</span>
            </div>
            <div className="rounded-lg border border-border p-3 bg-surface">
              <span className="font-bold text-foreground block">Payment Processing</span>
              <span className="text-muted-foreground mt-0.5 block">Stripe / LemonSqueezy for secure PCI-compliant credit card processing. Virality never stores full credit card numbers.</span>
            </div>
            <div className="rounded-lg border border-border p-3 bg-surface">
              <span className="font-bold text-foreground block">AI Model Inference</span>
              <span className="text-muted-foreground mt-0.5 block">OpenAI, Anthropic, and Google Gemini for real-time script generation under enterprise terms.</span>
            </div>
            <div className="rounded-lg border border-border p-3 bg-surface">
              <span className="font-bold text-foreground block">Transactional Email</span>
              <span className="text-muted-foreground mt-0.5 block">Resend / SendGrid for one-time login verification codes, password resets, and critical account alerts.</span>
            </div>
          </div>
        </section>

        {/* Section 5: Security */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground flex items-center gap-2">
            <Lock className="h-4 w-4 text-accent" /> 5. Data Security Standards
          </h2>
          <p>
            We implement state-of-the-art security measures to protect your creative and personal data:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li><strong>Encryption in Transit:</strong> All data transmitted between your browser and our servers is encrypted using modern TLS 1.3 encryption.</li>
            <li><strong>Encryption at Rest:</strong> Database records, authentication secrets, and sensitive tokens are encrypted using AES-256 standards.</li>
            <li><strong>Access Controls:</strong> Strict role-based access control (RBAC) and least-privilege principles are enforced for all production systems.</li>
          </ul>
        </section>

        {/* Section 6: Rights & Data Deletion */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground flex items-center gap-2">
            <Database className="h-4 w-4 text-accent" /> 6. Your Rights &amp; Data Deletion (GDPR &amp; CCPA)
          </h2>
          <p>
            Regardless of your physical location, we afford all creators the following fundamental data rights:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-xs">
            <li><strong>Right to Access &amp; Portability:</strong> You can export your drafts, scripts, and ideas at any time.</li>
            <li><strong>Right to Rectification:</strong> You can modify your profile, creator niche, and settings directly in the app.</li>
            <li><strong>Right to Erasure (&ldquo;Right to be Forgotten&rdquo;):</strong> You may request complete permanent deletion of your account and all associated scripts and tokens by emailing <a href="mailto:getintouch@viralityai.media" className="text-accent underline font-medium">getintouch@viralityai.media</a>. Requests are completed within 30 days.</li>
            <li><strong>Non-Discrimination:</strong> We will never discriminate against you or degrade your platform experience for exercising your privacy rights.</li>
          </ul>
        </section>

        {/* Section 7: Retention */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            7. Data Retention
          </h2>
          <p className="text-xs">
            We retain your workspace data, scripts, and settings as long as your account remains active. If your account is closed or deleted, we permanently erase or anonymize your personal information from production databases within 30 days, except where retention is strictly required to comply with statutory legal or tax obligations.
          </p>
        </section>

        {/* Section 8: Children's Privacy */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            8. Children&rsquo;s Privacy
          </h2>
          <p className="text-xs">
            Virality AI is not intended for or directed toward individuals under the age of 13 (or under 16 in certain European jurisdictions). We do not knowingly collect personal data from minors. If you believe a child has provided us with personal information, please contact us immediately so we can promptly delete it.
          </p>
        </section>

        {/* Section 9: Changes & Contact */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            9. Contact Us
          </h2>
          <p className="text-xs">
            If you have questions regarding this Privacy Policy, your connected channels, or our security practices, please reach out to our privacy officer:
          </p>
          <div className="rounded-xl border border-border bg-surface p-4 text-xs">
            <p className="font-bold text-foreground">Virality Team</p>
            <p className="mt-1">Email: <a href="mailto:getintouch@viralityai.media" className="text-accent underline font-medium">getintouch@viralityai.media</a></p>
          </div>
        </section>
      </div>
    </LegalLayout>
  );
}
